import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dropdown,
  makeStyles,
  Option,
  SearchBox,
  Switch,
  TableCellLayout,
  tokens,
  type DropdownProps,
  type TableColumnDefinition,
} from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import { ROLES } from '../constants/roles';
import '../index.css';
import { DataService } from '../services/dataService';
import type { IDataItem } from '../models/IDataItem';
import type { IPersonalListItem } from '../models/IPersonalListItem';
const useStyles = makeStyles({
  container: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: '100%',
    overflow: 'auto',
  },
  dataGridHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

const ROLE_STORAGE_KEY = 'userPersonalRole';
const getCurrentRole = (): string[] => {
  try {
    const raw = sessionStorage.getItem(ROLE_STORAGE_KEY);
    console.log('Dashboard getCurrentRole - raw sessionStorage:', raw);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map((r) => r.trim());
    }

    return [];
  } catch (error) {
    console.log('Error parsing role:', error);
    return [];
  }
};

const Dashboard: React.FC = () => {
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const styles = useStyles();
  const isDashboardRoot = location.pathname === '/';
  const navigate = useNavigate();
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);

  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const STORAGE_FILTER_KEY = 'personalDashboardFilters';
  const [filterText, setFilterText] = useState('');
  const [personTypeFilter, setPersonTypeFilter] = useState('Alle');
  const [taetigkeiten, setTaetigkeiten] = useState([] as IDataItem[]);
  const [selectedTaetigkeiten, setSelectedTaetigkeiten] = useState(
    [] as string[]
  );
  const [inaktiv, setInaktiv] = useState(false);
  const [items, setItems] = useState([] as IPersonalListItem[]);

  useEffect(() => {
    const handleRoleChange = () => {
      console.log('Dashboard detected role change event');
      const newRoles = getCurrentRole();
      console.log('New roles after event:', newRoles);
      setRoles(newRoles);
    };

    console.log('Dashboard mounted, initial roles:', roles);

    // Check if role was set between mounting and useEffect running
    const currentRoles = getCurrentRole();
    if (currentRoles.length > 0 && roles.length === 0) {
      console.log(
        'Role was set before event listener - updating now:',
        currentRoles
      );
      setRoles(currentRoles);
    }

    window.addEventListener('role-changed', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);

    return () => {
      window.removeEventListener('role-changed', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [roles]);

  // Case-insensitive role checking
  const hasRole = (roleName: string) =>
    roles.some((r) => r.toLowerCase() === roleName.toLowerCase());

  const hasPermission = ROLES.some((allowedRole) => hasRole(allowedRole));

  const saveFiltersToStorage = () => {
    const filterState = {
      filterText,
      personTypeFilter,
      selectedTaetigkeiten,
      inaktiv,
    };
    localStorage.setItem(STORAGE_FILTER_KEY, JSON.stringify(filterState));
  };

  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = localStorage.getItem(STORAGE_FILTER_KEY);
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        setFilterText(parsedFilters.filterText || '');
        setPersonTypeFilter(parsedFilters.personTypeFilter || 'Alle');
        setSelectedTaetigkeiten(parsedFilters.selectedTaetigkeiten || []);
        setInaktiv(parsedFilters.inaktiv || false);
        return true;
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return false;
  };

  React.useEffect(() => {
    loadFiltersFromStorage();
  }, []);

  React.useEffect(() => {
    if (
      filterText === '' &&
      personTypeFilter === 'Alle' &&
      selectedTaetigkeiten.length === 0 &&
      inaktiv === false
    ) {
      localStorage.removeItem(STORAGE_FILTER_KEY);
      return;
    }

    saveFiltersToStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, personTypeFilter, selectedTaetigkeiten, inaktiv]);

  React.useEffect(() => {
    const fetchFilterOptions = async (): Promise<void> => {
      if (!dataService) return;
      const response = await dataService.getBerufsartliste(personTypeFilter);
      if (response) setTaetigkeiten(response);
    };
    fetchFilterOptions();
  }, [personTypeFilter, dataService]);

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      // Only search if filterText is empty or has more than 3 characters
      if (filterText.length > 0 && filterText.length <= 3) return;

      const response = await dataService.getPersonalliste(
        personTypeFilter,
        selectedTaetigkeiten,
        inaktiv,
        filterText
      );
      if (response) setItems(response);
    };
    fetchItems();
  }, [
    personTypeFilter,
    selectedTaetigkeiten,
    inaktiv,
    filterText,
    dataService,
  ]);

  const onOptionSelect = React.useCallback<
    NonNullable<DropdownProps['onOptionSelect']>
  >((_, data) => {
    setSelectedTaetigkeiten([]);
    setPersonTypeFilter(
      data.selectedOptions.length > 0 ? data.selectedOptions[0] : 'Alle'
    );
  }, []);

  const onTaetigkeitSelect = React.useCallback<
    NonNullable<DropdownProps['onOptionSelect']>
  >((_, data) => {
    setSelectedTaetigkeiten(data.selectedOptions);
  }, []);

  const columns: TableColumnDefinition<IPersonalListItem>[] = [
    createTableColumn<IPersonalListItem>({
      columnId: 'name',
      renderHeaderCell: () => 'Name',
      compare: (a, b) => {
        const { firstName: firstNameA, lastName: lastNameA } = {
          firstName: a.vorname || '',
          lastName: a.nachname || '',
        };
        const { firstName: firstNameB, lastName: lastNameB } = {
          firstName: b.vorname || '',
          lastName: b.nachname || '',
        };

        const lastNameComparison = lastNameA.localeCompare(lastNameB);

        if (lastNameComparison === 0) {
          return firstNameA.localeCompare(firstNameB);
        }

        return lastNameComparison;
      },
      renderCell: (item) => (
        <TableCellLayout>{item.name ?? ''}</TableCellLayout>
      ),
    }),
    createTableColumn<IPersonalListItem>({
      columnId: 'svnr',
      renderHeaderCell: () => 'SVNR',
      renderCell: (item) => (
        <TableCellLayout>{item.svnr ?? ''}</TableCellLayout>
      ),
    }),
    createTableColumn<IPersonalListItem>({
      columnId: 'personalnr',
      renderHeaderCell: () => 'Personalnummer',
      renderCell: (item) => (
        <TableCellLayout>{item.personalnr ?? ''}</TableCellLayout>
      ),
    }),
    createTableColumn<IPersonalListItem>({
      columnId: 'strMeldedatum',
      compare: (a, b) => {
        const dateA = a.meldedatum ? new Date(a.meldedatum) : null;
        const dateB = b.meldedatum ? new Date(b.meldedatum) : null;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.getTime() - dateB.getTime();
      },
      renderHeaderCell: () => 'Meldedatum',
      renderCell: (item) => (
        <TableCellLayout>{item.strMeldedatum ?? ''}</TableCellLayout>
      ),
    }),
    createTableColumn<IPersonalListItem>({
      columnId: 'strAustrittsdatum',
      compare: (a, b) => {
        const dateA = a.austrittsdatum ? new Date(a.austrittsdatum) : null;
        const dateB = b.austrittsdatum ? new Date(b.austrittsdatum) : null;
        if (!dateA && !dateB) return 0;
        if (!dateA) return -1;
        if (!dateB) return 1;
        return dateA.getTime() - dateB.getTime();
      },
      renderHeaderCell: () => 'Austritt',
      renderCell: (item) => (
        <TableCellLayout>{item.strAustrittsdatum ?? ''}</TableCellLayout>
      ),
    }),
    createTableColumn<IPersonalListItem>({
      columnId: 'abteilung',
      compare: (a, b) => (a.abteilung || '').localeCompare(b.abteilung || ''),
      renderHeaderCell: () => 'Abteilung',
      renderCell: (item) => (
        <TableCellLayout>{item.abteilung ?? ''}</TableCellLayout>
      ),
    }),
    createTableColumn<IPersonalListItem>({
      columnId: 'taetigkeit',
      compare: (a, b) => (a.taetigkeit || '').localeCompare(b.taetigkeit || ''),
      renderHeaderCell: () => 'Tätigkeit',
      renderCell: (item) => (
        <TableCellLayout>{item.taetigkeit ?? ''}</TableCellLayout>
      ),
    }),
  ];

  const handleRowClick = (item: IPersonalListItem) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state: any = {
      personalGuid: item.guid,
      meldedatum: item.meldedatum,
      name: item.name,
    };

    navigate(`/detail`, { state });
    window.scrollTo(0, 0);
  };

  return (
    <>
      {isDashboardRoot && roles.length > 0 ? (
        hasPermission ? (
          <div className={styles.container}>
            <div className="searchContainer">
              <SearchBox
                placeholder="Suche nach Nachname, Vorname, Personalnummer..."
                onChange={(_, newValue) => setFilterText(newValue?.value || '')}
                className="searchBox"
                value={filterText}
              />
            </div>
            <div className="dropdownContainer" style={{ marginTop: '8px' }}>
              <Dropdown
                title="Filtern nach Bereich"
                placeholder="Filtern nach Bereich"
                value={personTypeFilter}
                onOptionSelect={onOptionSelect}
                style={{ width: '250px' }}
              >
                <Option>Alle</Option>
                <Option>Verwaltung</Option>
                <Option>MitarbeiterInnen</Option>
                <Option>Lehre</Option>
              </Dropdown>
              <Dropdown
                title="Filtern nach Tätigkeit"
                placeholder="Filtern nach Tätigkeit"
                clearable
                multiselect={true}
                selectedOptions={selectedTaetigkeiten}
                value={
                  selectedTaetigkeiten.length > 0
                    ? selectedTaetigkeiten
                        .map(
                          (id) =>
                            taetigkeiten.find((t) => t.value === id)?.text || id
                        )
                        .join(', ')
                    : ''
                }
                onOptionSelect={onTaetigkeitSelect}
                style={{ marginLeft: '10px', width: '400px' }}
              >
                {taetigkeiten.map((t) => (
                  <Option key={t.value} value={t.value}>
                    {t.text}
                  </Option>
                ))}
              </Dropdown>
              <Switch
                label={inaktiv ? 'Inaktive anzeigen' : 'Inaktive ausblenden'}
                checked={inaktiv}
                onChange={(_, checked) =>
                  setInaktiv(checked !== undefined ? checked.checked : false)
                }
              />
            </div>
            {items.length > 0 ? (
              <div className="dataGridContainer">
                <DataGrid
                  items={items}
                  columns={columns}
                  sortable
                  size="medium"
                  aria-label="Dashboard"
                  focusMode="composite"
                  style={{ minWidth: '550px' }}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell className="dataGridHeaderCell">
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<IPersonalListItem>>
                    {({ item, rowId }) => (
                      <DataGridRow key={rowId}>
                        {(column) => (
                          <DataGridCell
                            className="dataGridCell"
                            onClick={() => {
                              handleRowClick(item as IPersonalListItem);
                            }}
                          >
                            {column.renderCell(item)}
                          </DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </div>
            ) : (
              <p
                style={{
                  fontSize: '16px',
                  marginBottom: '30px',
                  maxWidth: '500px',
                }}
              >
                Keine Personen gefunden.
              </p>
            )}
          </div>
        ) : (
          <div className={styles.container}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center',
              }}
            >
              <h1 style={{ color: '#d13438', marginBottom: '20px' }}>
                Zugriff verweigert
              </h1>
              <p
                style={{
                  fontSize: '16px',
                  marginBottom: '30px',
                  maxWidth: '500px',
                }}
              >
                Sie haben keine Berechtigung, auf dieses Dashboard zuzugreifen.
                Bitte kontaktieren Sie einen Administrator, wenn Sie glauben,
                dass dies ein Fehler ist.
              </p>
            </div>
          </div>
        )
      ) : (
        <></>
      )}
    </>
  );
};
export default Dashboard;
